import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Avatar,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import useSWR from 'swr';
import { isAxiosError } from 'axios';
import {
  AccountSetupMode,
  DRIVER_CATEGORY_OPTIONS,
  DriverCategory,
  DriverStatus,
  UserRole,
  displayDriverLogin,
  formatDriverCategory,
} from '@kangaroo/shared';
import { SectionCard, StatusBadge, colors } from '@kangaroo/ui';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import { DriverProfileSkeleton } from '../../components/loading/PageSkeletons';
import { UserAvatarUpload } from '../../components/settings/UserAvatarUpload';

import { driverDetailKey, fetchDriver, linkDriverParent, releaseDriver, updateDriver, uploadDriverAvatar } from '../../features/drivers';
import { currentTeamKey, fetchCurrentTeam } from '../../features/teams';
import { isMinor } from '../../lib/driverUtils';
import { canManageDrivers } from '../../lib/roles';
import { viewerIsTeamWorkspaceOwner } from '../../lib/teamWorkspace';
import { useAuthStore } from '../../store/authStore';

function driverBadgeStatus(status: DriverStatus): 'active' | 'injured' | 'inactive' {
  if (status === DriverStatus.INJURED) return 'injured';
  if (status === DriverStatus.ACTIVE) return 'active';
  return 'inactive';
}

function driverBadgeLabel(status: DriverStatus): string | undefined {
  if (status === DriverStatus.GRADUATED) return 'Graduated';
  return undefined;
}

function getApiErrorMessage(error: unknown) {
  if (isAxiosError(error)) {
    return (error.response?.data as { message?: string } | undefined)?.message ?? error.message;
  }
  return error instanceof Error ? error.message : 'Request failed';
}

function formatDateOfBirth(value: string | null) {
  if (!value) return '—';
  const [year, month, day] = value.split('-');
  if (!year || !month || !day) return value;
  return `${month}/${day}/${year}`;
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75 }}>
      <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{label}</Typography>
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, textAlign: 'right' }}>{value}</Typography>
    </Box>
  );
}

interface DriverDetailContentProps {
  driverId: string;
  onReleased?: () => void;
}

export function DriverDetailContent({ driverId, onReleased }: DriverDetailContentProps) {
  const user = useAuthStore((state) => state.user);
  const { data: team } = useSWR(user?.teamId ? currentTeamKey : null, fetchCurrentTeam);
  const isWorkspaceOwner = viewerIsTeamWorkspaceOwner(user, team);
  const canManage = user ? canManageDrivers(user.role, { isTeamWorkspaceOwner: isWorkspaceOwner }) : false;
  const canRelease = canManage;
  const [releaseConfirmOpen, setReleaseConfirmOpen] = useState(false);
  const [addParentOpen, setAddParentOpen] = useState(false);
  const [addParentForm, setAddParentForm] = useState({ name: '', email: '', applyToAllLinkedDrivers: false });
  const [addParentSubmitting, setAddParentSubmitting] = useState(false);
  const [addParentError, setAddParentError] = useState<string | null>(null);
  const [releasing, setReleasing] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    loginMode: 'email' as 'email' | 'username',
    email: '',
    username: '',
    dateOfBirth: '',
    driverNumber: '',
    licenseNumber: '',
    category: DriverCategory.SENIOR,
  });
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);

  const {
    data: driver,
    error,
    isLoading,
    mutate,
  } = useSWR(driverDetailKey(driverId), () => fetchDriver(driverId));

  function openEditDialog() {
    if (!driver) {
      return;
    }

    setEditForm({
      name: driver.name,
      loginMode: driver.username ? 'username' : 'email',
      email: driver.username ? '' : driver.email,
      username: driver.username ?? '',
      dateOfBirth: driver.dateOfBirth ?? '',
      driverNumber: driver.driverNumber ?? '',
      licenseNumber: driver.licenseNumber ?? '',
      category: driver.category,
    });
    setEditError(null);
    setEditOpen(true);
  }

  async function handleSaveProfile() {
    if (!driver) {
      return;
    }

    if (editForm.name.trim().length < 2) {
      setEditError('Driver name is required');
      return;
    }

    if (driver.profileKind !== 'historical' && editForm.loginMode === 'email' && !editForm.email.trim()) {
      setEditError('Driver email is required');
      return;
    }

    if (driver.profileKind !== 'historical' && editForm.loginMode === 'username' && !editForm.username.trim()) {
      setEditError('Driver username is required');
      return;
    }

    try {
      setEditSubmitting(true);
      setEditError(null);
      const updated = await updateDriver(driver.id, {
        name: editForm.name.trim(),
        email: driver.profileKind !== 'historical' && editForm.loginMode === 'email' ? editForm.email.trim() : undefined,
        username: driver.profileKind !== 'historical' && editForm.loginMode === 'username' ? editForm.username.trim() : undefined,
        dateOfBirth: editForm.dateOfBirth || null,
        driverNumber: editForm.driverNumber.trim() || null,
        licenseNumber: editForm.licenseNumber.trim() || null,
        category: editForm.category,
      });
      await mutate(updated, false);
      setEditOpen(false);
      setSnackbarMessage('Driver profile updated');
    } catch (saveError) {
      setEditError(getApiErrorMessage(saveError));
    } finally {
      setEditSubmitting(false);
    }
  }

  async function handleAvatarUpload(file: File) {
    if (!driver) {
      return;
    }

    try {
      setAvatarUploading(true);
      setEditError(null);
      const updated = await uploadDriverAvatar(driver.id, file);
      await mutate(updated, false);
      setSnackbarMessage('Driver photo updated');
    } catch (uploadError) {
      setEditError(getApiErrorMessage(uploadError));
    } finally {
      setAvatarUploading(false);
    }
  }

  async function handleLinkParent() {
    if (!driver) {
      return;
    }

    if (addParentForm.name.trim().length < 2) {
      setAddParentError('Parent name is required');
      return;
    }
    if (!addParentForm.email.trim()) {
      setAddParentError('Parent email is required');
      return;
    }
    if (driver.parents.some((parent) => parent.email.toLowerCase() === addParentForm.email.trim().toLowerCase())) {
      setAddParentError('This parent is already linked');
      return;
    }

    try {
      setAddParentSubmitting(true);
      setAddParentError(null);
      await linkDriverParent(driver.id, {
        name: addParentForm.name.trim(),
        email: addParentForm.email.trim(),
        setupMode: AccountSetupMode.REGISTRATION_LINK,
        applyToAllLinkedDrivers: addParentForm.applyToAllLinkedDrivers,
      });
      setAddParentOpen(false);
      setAddParentForm({ name: '', email: '', applyToAllLinkedDrivers: false });
      setSnackbarMessage(addParentForm.applyToAllLinkedDrivers ? 'Parent linked to your drivers' : 'Parent linked successfully');
      await mutate();
    } catch (linkError) {
      setAddParentError(getApiErrorMessage(linkError));
    } finally {
      setAddParentSubmitting(false);
    }
  }

  async function handleReleaseDriver() {
    if (!driver) {
      return;
    }

    try {
      setReleasing(true);
      await releaseDriver(driver.id);
      setReleaseConfirmOpen(false);
      setSnackbarMessage(`${driver.name} released. They can join another team or create their own workspace.`);
      await mutate();
      onReleased?.();
    } catch (releaseError) {
      setSnackbarMessage(getApiErrorMessage(releaseError));
    } finally {
      setReleasing(false);
    }
  }

  if (isLoading) {
    return <DriverProfileSkeleton />;
  }

  if (error || !driver) {
    return <Alert severity="error">{getApiErrorMessage(error ?? new Error('Driver not found'))}</Alert>;
  }

  const canShowRelease = canRelease && driver.status === DriverStatus.ACTIVE;
  const canEditProfile = canManage
    || (user?.role === UserRole.DRIVER && driver.userId === user.id)
    || (user?.role === UserRole.PARENT && driver.parents.some((parent) => parent.userId === user.id));
  const canLinkParent = user?.role === UserRole.PARENT && driver.parents.some((parent) => parent.userId === user.id);
  const isDriverMinor = driver.dateOfBirth
    ? isMinor(driver.dateOfBirth)
    : driver.age !== null && driver.age < 18;
  const minorParentMessage = driver.teamManaged
    ? 'This minor driver is managed directly by the team; no parent account is required.'
    : 'No parent linked yet — required for drivers under 18.';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'center', mb: 3 }}>
        <Avatar src={driver.avatarUrl ?? undefined} sx={{ width: 64, height: 64, fontSize: '1.25rem', border: `3px solid ${colors.primary[500]}` }}>
          {driver.initials}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="h5" fontWeight={700}>{driver.name}</Typography>
            <StatusBadge status={driverBadgeStatus(driver.status)} customLabel={driverBadgeLabel(driver.status)} />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {driver.profileKind === 'historical' ? 'Historical driver · no login account' : `Mobile login: ${displayDriverLogin(driver)}`}
          </Typography>
        </Box>
        {canEditProfile ? (
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={openEditDialog}
            sx={{ textTransform: 'none', fontSize: '0.74rem', fontWeight: 800 }}
          >
            Edit profile
          </Button>
        ) : null}
      </Box>

      <Stack spacing={2.5} sx={{ flex: 1 }}>
        <SectionCard title="Driver Account">
          <InfoRow label={driver.profileKind === 'historical' ? 'Profile' : driver.username ? 'Username' : 'Email'} value={driver.profileKind === 'historical' ? 'Historical · no login' : displayDriverLogin(driver)} />
          <InfoRow label="Date of birth" value={formatDateOfBirth(driver.dateOfBirth)} />
          <InfoRow label="Age" value={driver.age !== null ? String(driver.age) : '—'} />
          <InfoRow label="Category" value={formatDriverCategory(driver.category)} />
          <InfoRow label="Driver number" value={driver.driverNumber ?? '—'} />
          <InfoRow label="License" value={driver.licenseNumber ?? '—'} />
          {driver.teamManaged ? <InfoRow label="Management" value="Team-managed" /> : null}
          <Divider sx={{ my: 1.5 }} />
          <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
            This account is used on the mobile app to scan and sync MyChron telemetry.
          </Typography>
        </SectionCard>

        {isDriverMinor ? (
          <SectionCard title="Parent / Guardian">
            {driver.parents.length > 0 ? (
              <Stack spacing={1.5} divider={<Divider flexItem />}>
                {driver.parents.map((parent) => (
                  <Box key={parent.id}>
                    <InfoRow label="Name" value={parent.name} />
                    <InfoRow label="Email" value={parent.email} />
                  </Box>
                ))}
                <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                  Parent accounts can sign in on mobile to view linked minor drivers.
                </Typography>
                {canLinkParent ? (
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{ mt: 1.5, textTransform: 'none', fontSize: '0.72rem' }}
                    onClick={() => {
                      setAddParentError(null);
                      setAddParentOpen(true);
                    }}
                  >
                    Add parent
                  </Button>
                ) : null}
              </Stack>
            ) : (
              <Stack spacing={1.5}>
                <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                  {minorParentMessage}
                </Typography>
                {canLinkParent && !driver.teamManaged ? (
                  <Button
                    size="small"
                    variant="contained"
                    sx={{ alignSelf: 'flex-start', textTransform: 'none', fontSize: '0.72rem' }}
                    onClick={() => {
                      setAddParentError(null);
                      setAddParentOpen(true);
                    }}
                  >
                    Add parent
                  </Button>
                ) : null}
              </Stack>
            )}
          </SectionCard>
        ) : null}
      </Stack>

      {canShowRelease ? (
        <Box
          sx={{
            mt: 3,
            borderTop: '1px solid',
            borderColor: 'rgba(239,68,68,0.28)',
            bgcolor: 'rgba(239,68,68,0.06)',
            borderRadius: 1.2,
            px: { xs: 1.2, sm: 1.5 },
            py: 1.25,
          }}
        >
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 900, color: '#FCA5A5' }}>
            Release driver
          </Typography>
          <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: 0.45, mb: 1.05, lineHeight: 1.5, maxWidth: 720 }}>
            Release removes this driver from your team roster and unassigns them from vehicles. Their login stays active so they can request to join another team, link to a parent team, or create a standalone workspace.
          </Typography>
          <Button
            size="small"
            color="error"
            variant="outlined"
            disabled={releasing}
            onClick={() => setReleaseConfirmOpen(true)}
            sx={{ textTransform: 'none', fontSize: '0.72rem' }}
          >
            Release
          </Button>
        </Box>
      ) : null}

      <Dialog open={releaseConfirmOpen} onClose={() => { if (!releasing) setReleaseConfirmOpen(false); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Release {driver.name}?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.84rem', color: 'text.secondary', lineHeight: 1.55 }}>
            This driver will leave your team roster and be unassigned from any vehicles. They can use their existing mobile login to join another team or set up their own workspace.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setReleaseConfirmOpen(false)} disabled={releasing} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={releasing}
            onClick={() => void handleReleaseDriver()}
            sx={{ textTransform: 'none', fontWeight: 800 }}
          >
            {releasing ? 'Releasing...' : 'Release'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => { if (!editSubmitting && !avatarUploading) setEditOpen(false); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Edit driver profile</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            {editError ? <Alert severity="error">{editError}</Alert> : null}
            {driver.profileKind !== 'historical' && <UserAvatarUpload
              name={editForm.name || driver.name}
              initials={driver.initials}
              avatarUrl={driver.avatarUrl}
              uploading={avatarUploading}
              onPickFile={(file) => void handleAvatarUpload(file)}
            />}
            <TextField
              label="Full name"
              value={editForm.name}
              onChange={(event) => setEditForm((current) => ({ ...current, name: event.target.value }))}
              required
              fullWidth
              size="small"
            />
            {driver.profileKind !== 'historical' && <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                select
                label="Login type"
                value={editForm.loginMode}
                onChange={(event) => setEditForm((current) => ({
                  ...current,
                  loginMode: event.target.value as 'email' | 'username',
                  email: event.target.value === 'email' ? current.email : '',
                  username: event.target.value === 'username' ? current.username || (driver.username ?? '') : '',
                }))}
                size="small"
                sx={{ minWidth: { sm: 150 } }}
              >
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="username">Username</MenuItem>
              </TextField>
              {editForm.loginMode === 'email' ? (
                <TextField
                  label="Email"
                  type="email"
                  value={editForm.email}
                  onChange={(event) => setEditForm((current) => ({ ...current, email: event.target.value }))}
                  required
                  fullWidth
                  size="small"
                />
              ) : (
                <TextField
                  label="Username"
                  value={editForm.username}
                  onChange={(event) => setEditForm((current) => ({ ...current, username: event.target.value.replace(/\s+/g, '') }))}
                  required
                  fullWidth
                  size="small"
                  helperText="3-32 characters; letters, numbers, dots, underscores, or hyphens"
                />
              )}
            </Stack>}
            <TextField
              label="Date of birth"
              type="date"
              value={editForm.dateOfBirth}
              onChange={(event) => setEditForm((current) => ({ ...current, dateOfBirth: event.target.value }))}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                label="Driver number"
                value={editForm.driverNumber}
                onChange={(event) => setEditForm((current) => ({ ...current, driverNumber: event.target.value }))}
                fullWidth
                size="small"
              />
              <TextField
                label="License"
                value={editForm.licenseNumber}
                onChange={(event) => setEditForm((current) => ({ ...current, licenseNumber: event.target.value }))}
                fullWidth
                size="small"
              />
            </Stack>
            <TextField
              select
              label="Category"
              value={editForm.category}
              onChange={(event) => setEditForm((current) => ({ ...current, category: event.target.value as DriverCategory }))}
              fullWidth
              size="small"
            >
              {DRIVER_CATEGORY_OPTIONS.map((category) => (
                <MenuItem key={category} value={category}>
                  {formatDriverCategory(category)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setEditOpen(false)} disabled={editSubmitting || avatarUploading} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={editSubmitting || avatarUploading}
            onClick={() => void handleSaveProfile()}
            sx={{ textTransform: 'none', fontWeight: 800 }}
          >
            {editSubmitting ? 'Saving...' : 'Save profile'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addParentOpen} onClose={() => { if (!addParentSubmitting) setAddParentOpen(false); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Add parent / guardian</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            {addParentError ? <Alert severity="error">{addParentError}</Alert> : null}
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', lineHeight: 1.5 }}>
              Link an existing parent account by email, or send a setup link if they do not have an account yet.
            </Typography>
            <TextField
              label="Parent name"
              value={addParentForm.name}
              onChange={(event) => setAddParentForm((current) => ({ ...current, name: event.target.value }))}
              required
              fullWidth
              size="small"
            />
            <TextField
              label="Parent email"
              type="email"
              value={addParentForm.email}
              onChange={(event) => setAddParentForm((current) => ({ ...current, email: event.target.value }))}
              required
              fullWidth
              size="small"
            />
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Checkbox
                checked={addParentForm.applyToAllLinkedDrivers}
                onChange={(event) => setAddParentForm((current) => ({ ...current, applyToAllLinkedDrivers: event.target.checked }))}
                sx={{ p: 0.2, mt: 0.1 }}
              />
              <Box>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 800 }}>Apply to all my linked drivers</Typography>
                <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', lineHeight: 1.45 }}>
                  The parent will be linked to each driver currently visible to your parent account.
                </Typography>
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setAddParentOpen(false)} disabled={addParentSubmitting} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={addParentSubmitting}
            onClick={() => void handleLinkParent()}
            sx={{ textTransform: 'none', fontWeight: 800 }}
          >
            {addParentSubmitting ? 'Adding…' : 'Add parent'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snackbarMessage)}
        autoHideDuration={3200}
        onClose={() => setSnackbarMessage(null)}
        message={snackbarMessage ?? ''}
      />
    </Box>
  );
}
